// Helper to extract mode-specific tokens from the full design tokens object.

import gdsTokens from '@style-dictionary/fetched-tokens/tokens/gds-tokens';
import themeTokens from '@style-dictionary/fetched-tokens/tokens/theme-tokens';
import type { Token } from 'style-dictionary';
import type { BannerMode, ThemeMode } from './types/modes';

// Recursively map the design tokens type, replacing any mode-leaf structure
// (an object whose keys are exactly the banners) with the Style Dictionary Token type.
type ReplaceModeLeaves<T, L> = T extends L
    ? Token
    : T extends object
      ? { [K in keyof T]: ReplaceModeLeaves<T[K], L> }
      : T;

type BrandLeafStructure = {
    [B in BannerMode]: string;
};

type ThemeLeafStructure = {
    [T in ThemeMode]: string;
};

export type GDSTokens = ReplaceModeLeaves<typeof gdsTokens, BrandLeafStructure>;
export type ThemeTokens = ReplaceModeLeaves<typeof themeTokens, ThemeLeafStructure>;

// Simple list of available banners. A Set isn't necessary since we only iterate and check inclusion of keys.
const BANNERS: readonly BannerMode[] = ['Base', 'Matas', 'Kicks'];
const THEMES: readonly ThemeMode[] = [
    'neutral',
    'club-1',
    'club-2',
    'campaign-1',
    'campaign-2',
    'campaign-3',
    'sun',
    'christmas',
    'black-friday'
];

// Detect an object whose keys are exactly (or at least) the banners (e.g. { Base: ..., Matas: ..., Kicks: ... })
function isModeValueObject<T extends string>(value: unknown): value is Record<T, unknown> {
    if (!value || typeof value !== 'object') return false;
    // We only need the value to contain every banner key; it may include additional meta keys which we ignore.
    return (
        BANNERS.every((b) => b in (value as Record<string, unknown>)) ||
        THEMES.every((t) => t in (value as Record<string, unknown>))
    );
}

function extractModeTokens<T extends string>(mode: T, source: unknown) {
    if (!source || typeof source !== 'object') return {}; // primitives produce no direct output here

    return Object.entries(source as Record<string, unknown>).reduce((acc, [key, value]) => {
        // FIRST: if this entry is a mode value object, pick the mode-specific value
        if (isModeValueObject(value)) {
            acc[key] = value[mode];
            return acc; // done with this key
        }

        // OTHERWISE: if it's another nested object/collection, recurse.
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = extractModeTokens(mode, value);
            // Only assign if nested produced something (avoid sprinkling empty objects)
            if (Object.keys(nested).length > 0) {
                acc[key] = nested;
            }
        }

        return acc;
    }, {});
}

export function getBrandTokens<B extends BannerMode>(brand: B): GDSTokens {
    // Runtime object built by extraction; asserted to the mapped token shape.
    return extractModeTokens(brand, gdsTokens) as GDSTokens;
}

export function getThemeTokens<T extends ThemeMode>(theme: T): ThemeTokens {
    // Runtime object built by extraction; asserted to the mapped token shape.
    return extractModeTokens(theme, themeTokens) as ThemeTokens;
}
